import React, { useState, useEffect, useMemo } from 'react';
import { Col, FormGroup, Input, Row, Label, Button } from 'reactstrap';
import BootstrapTable from 'react-bootstrap-table-next';
import Loading from '../../components/Loading/Loading';
import paginationFactory from 'react-bootstrap-table2-paginator';
import moment from 'moment';
import axios from 'axios';
import { SingleDatePicker } from 'react-dates';
import 'react-dates/lib/css/_datepicker.css';
import {
  LOAD_SHAPE_TYPE_OPTIONS,
  GSP_GROUP_OPTIONS,
  CONNECTION_TYPE_OPTIONS,
  MARKET_SEGMENT_OPTIONS,
  DOMESTIC_INDICATOR_OPTIONS,
  PRODUCT_OPTIONS,
} from './constants';

const LoadShapingService = () => {
  const [loading, setLoading] = useState(false);
  const [tableData, setTableData] = useState([]);

  // Pagination state
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(10);
  const [totalSize, setTotalSize] = useState(0);
  const [sortBy, setSortBy] = useState('created');
  const [sortDir, setSortDir] = useState('desc');

  // Filter state
  const [filters, setFilters] = useState({
    loadShapeType: '',
    settlementDate: '',
    gspGroupId: '',
    connectionType: '',
    marketSegment: '',
    domesticIndicator: '',
    settlementProduct: '',
  });

  // Date picker state
  const [settlementDateFocused, setSettlementDateFocused] = useState(false);

  // Common header style for all columns
  const commonHeaderStyle = {
    whiteSpace: 'normal',
    lineHeight: '1.2',
  };

  // Common date formatter
  const formatDate = (cell) => (cell ? moment(cell).format('DD/MM/YYYY') : '');

  // Common date sort value
  const getDateSortValue = (cell) => (cell ? moment(cell).valueOf() : 0);

  // Common option formatter factory
  const createOptionFormatter = (options) => (cell) => {
    const option = options.find((opt) => opt.value === cell);
    return option ? option.label : cell;
  };

  const columns = useMemo(
    () => [
      {
        dataField: 'loadShapeType',
        text: 'Type',
        sort: true,
        headerStyle: commonHeaderStyle,
        formatter: createOptionFormatter(LOAD_SHAPE_TYPE_OPTIONS),
      },
      {
        dataField: 'versionDate',
        text: 'Received On',
        headerStyle: commonHeaderStyle,
        formatter: formatDate,
        sort: true,
        sortValue: getDateSortValue,
      },
      {
        dataField: 'settlementDate',
        text: 'Settlement Date',
        headerStyle: commonHeaderStyle,
        formatter: formatDate,
        sort: true,
        sortValue: getDateSortValue,
      },
      {
        dataField: 'gspGroupId',
        text: 'GSP Group',
        headerStyle: commonHeaderStyle,
        sort: true,
      },
      {
        dataField: 'connectionType',
        text: 'Connection Type',
        headerStyle: commonHeaderStyle,
        sort: true,
        formatter: createOptionFormatter(CONNECTION_TYPE_OPTIONS),
      },
      {
        dataField: 'marketSegment',
        text: 'Market Segment',
        headerStyle: commonHeaderStyle,
        sort: true,
        formatter: createOptionFormatter(MARKET_SEGMENT_OPTIONS),
      },
      {
        dataField: 'domesticIndicator',
        text: 'Domestic Indicator',
        headerStyle: commonHeaderStyle,
        sort: true,
        formatter: createOptionFormatter(DOMESTIC_INDICATOR_OPTIONS),
      },
      {
        dataField: 'settlementProduct',
        text: 'Product',
        headerStyle: commonHeaderStyle,
        sort: true,
      },
      {
        dataField: 'content',
        text: 'Content',
        headerStyle: commonHeaderStyle,
        formatter: (cell) => {
          try {
            return (
              <pre
                style={{
                  whiteSpace: 'pre',
                  overflowX: 'auto',
                  maxHeight: '100px',
                  textAlign: 'left',
                  fontSize: '12px',
                }}
              >
                {JSON.stringify(cell, null, 2)}
              </pre>
            );
          } catch (e) {
            return cell;
          }
        },
      },
    ],
    [LOAD_SHAPE_TYPE_OPTIONS, CONNECTION_TYPE_OPTIONS, MARKET_SEGMENT_OPTIONS, DOMESTIC_INDICATOR_OPTIONS]
  );

  // Fetch data from API
  const fetchData = async () => {
    setLoading(true);

    try {
      const token = localStorage.token;

      // Build query parameters
      const params = {
        page: page - 1,
        size: size,
        sortBy: sortBy,
        sortDir: sortDir,
      };

      // Add filters only if they have values
      Object.keys(filters).forEach((key) => {
        if (filters[key] && filters[key].trim() !== '') {
          params[key] = filters[key];
        }
      });

      const response = await axios.get('/df/dataflows/rest/v1/load-shape/data', {
        params,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setTableData(response.data.content || []);
      setTotalSize(response.data.totalElements || 0);
    } catch (error) {
      setTableData([]);
      setTotalSize(0);
    }
    setLoading(false);
  };

  // Fetch data when page/size/sort/filters change
  useEffect(() => {
    fetchData();
  }, [page, size, sortBy, sortDir, filters]);

  // Table change handler
  const handleTableChange = (type, { page, sizePerPage, sortField, sortOrder }) => {
    if (type === 'sort') {
      setSortBy(sortField);
      setSortDir(sortOrder);
    }
    if (type === 'pagination') {
      setPage(page);
      setSize(sizePerPage);
    }
  };

  // Filter change handler
  const handleFilterChange = (filterName, value) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [filterName]: value,
    }));
    setPage(1); // Reset to first page on filter change
  };

  // Common filter clear handler
  const clearAllFilters = () => {
    setFilters({
      loadShapeType: '',
      settlementDate: '',
      gspGroupId: '',
      connectionType: '',
      marketSegment: '',
      domesticIndicator: '',
      settlementProduct: '',
    });
    setPage(1);
  };

  // Render select filter helper
  const renderSelectFilter = (id, label, value, options) => (
    <FormGroup>
      <Label for={id}>{label}</Label>
      <Input type='select' id={id} value={value} onChange={(e) => handleFilterChange(id, e.target.value)}>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </Input>
    </FormGroup>
  );

  return (
    <div className='organization'>
      <Row className='subheader'>
        <Col className='col-8'>
          <h1>Load Shaping Service</h1>
        </Col>
      </Row>

      <div className='page-cnt'>
        {/* Filters Section */}
        <Row className='mb-4'>
          <Col md={3}>
            {renderSelectFilter('loadShapeType', 'Type', filters.loadShapeType, LOAD_SHAPE_TYPE_OPTIONS)}
          </Col>

          <Col md={3}>
            <FormGroup>
              <Label for='settlementDate'>Settlement Date</Label>
              <SingleDatePicker
                id='settlementDate'
                date={filters.settlementDate ? moment(filters.settlementDate) : null}
                onDateChange={(date) => {
                  const dateValue = date ? date.startOf('day').toISOString() : '';
                  handleFilterChange('settlementDate', dateValue);
                }}
                focused={settlementDateFocused}
                onFocusChange={({ focused }) => setSettlementDateFocused(focused)}
                numberOfMonths={1}
                displayFormat='DD/MM/YYYY'
                placeholder='Select date'
                showClearDate={true}
              />
            </FormGroup>
          </Col>

          <Col md={3}>{renderSelectFilter('gspGroupId', 'GSP Group', filters.gspGroupId, GSP_GROUP_OPTIONS)}</Col>

          <Col md={3}>
            {renderSelectFilter('connectionType', 'Connection Type', filters.connectionType, CONNECTION_TYPE_OPTIONS)}
          </Col>
        </Row>

        <Row className='mb-4'>
          <Col md={3}>
            {renderSelectFilter('marketSegment', 'Market Segment', filters.marketSegment, MARKET_SEGMENT_OPTIONS)}
          </Col>

          <Col md={3}>
            {renderSelectFilter(
              'domesticIndicator',
              'Domestic Indicator',
              filters.domesticIndicator,
              DOMESTIC_INDICATOR_OPTIONS
            )}
          </Col>

          <Col md={3}>
            {renderSelectFilter('settlementProduct', 'Product', filters.settlementProduct, PRODUCT_OPTIONS)}
          </Col>

          <Col md={3} className='d-flex align-items-end'>
            <FormGroup>
              <Button color='primary' onClick={clearAllFilters}>
                Clear All Filters
              </Button>
            </FormGroup>
          </Col>
        </Row>

        {/* Table Section */}
        <Row>
          <Col className='col-12'>
            {loading && <Loading />}
            <BootstrapTable
              classes='table-hover'
              wrapperClasses='table-responsive'
              noDataIndication={() => <div>No records in table.</div>}
              columns={columns}
              data={tableData}
              keyField={'id'}
              bordered={false}
              hover
              remote={{
                filter: false,
                pagination: true,
                sort: true,
              }}
              pagination={paginationFactory({
                page: page,
                sizePerPage: size,
                totalSize: totalSize,
                sizePerPageList: [
                  { text: '5', value: 5 },
                  { text: '10', value: 10 },
                  { text: '20', value: 20 },
                  { text: '50', value: 50 },
                ],
              })}
              onTableChange={handleTableChange}
            />
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default LoadShapingService;
